import * as React from 'react';
import { useCallback } from 'react';
import cn from 'classnames';

import { Currency, CurrencySymbols } from '../../enums';
import { Money as MoneyType } from '../../types';
import { useTheme } from '../../theme';
import { getFormattedAmount } from '../utils';
import { useConfig } from '../../context';
import { MoneyRoundType } from '../types';
import Loader from '../../BaseComponents/Button/components/Loader/Loader';
import { CurrencyMetaMap } from '../constants';

export interface Props {
	money: MoneyType;
	moneyClassName?: string;
	amountClassName?: string;
	currencyClassName?: string;
	multiplyBy?: number;
	amountRenderer?: (amount: number, currency: string | Currency, isLoading?: boolean) => React.ReactNode;
	currencyRenderer?: (currency: CurrencySymbols) => React.ReactNode;
	showCurrency?: boolean;
	showCurrencyBeforeAmount?: boolean;
	roundType?: MoneyRoundType;
	isLoading?: boolean;
	strikethrough?: boolean;
}

export const Money: React.FunctionComponent<Props> = ({
	money,
	moneyClassName,
	amountClassName,
	currencyClassName,
	multiplyBy = 1,
	amountRenderer,
	currencyRenderer,
	showCurrency = true,
	showCurrencyBeforeAmount = false,
	roundType,
	isLoading = false,
	strikethrough = false
}) => {
	const { Money: css } = useTheme('Money');

	const {
		global: { roundMoneyAmount }
	} = useConfig();

	if (!money) {
		return null;
	}

	// TODO:
	const splitMoneyMin = 999;

	const moneyRoundType = roundType || (roundMoneyAmount && 'round') || null;

	const formattedAmount = React.useMemo(() => getFormattedAmount(money, multiplyBy, moneyRoundType, splitMoneyMin), [
		moneyRoundType,
		money,
		multiplyBy,
		splitMoneyMin
	]);

	const { symbol: currencySymbol, position } = CurrencyMetaMap[money.currency as Currency] ?? {};

	const hasCurrency = money && showCurrency;
	const showCurrencyBefore = position === 'before' || showCurrencyBeforeAmount;

	const renderCurrency = useCallback(() => {
		if (currencyRenderer) {
			return currencyRenderer(currencySymbol);
		}

		return (
			<span
				data-currency={money.currency}
				className={cn(currencyClassName, {
					[css.currency_before]: showCurrencyBefore,
					[css.currency_after]: !showCurrencyBefore
				})}
			>
				{currencySymbol}
			</span>
		);
	}, [currencySymbol, currencyRenderer, showCurrencyBefore]);

	return (
		<span
			className={cn(
				css.money,
				{ [css.money_loading]: isLoading, [css.money_strikethrough]: strikethrough },
				moneyClassName
			)}
		>
			{hasCurrency && showCurrencyBefore && renderCurrency()}

			{amountRenderer ? (
				amountRenderer(money.amount, money.currency, isLoading)
			) : isLoading ? (
				<Loader />
			) : (
				<span className={cn(css.amount, amountClassName)}>
					{formattedAmount
						? formattedAmount.split(' ').map((part, i) => <span key={i}>{part}</span>)
						: money?.currency
						? '0'
						: null}
				</span>
			)}

			{hasCurrency && !showCurrencyBefore && renderCurrency()}
		</span>
	);
};

export default Money;
